<template>
  <div
    ref="modalEl"
    class="modal fade"
    tabindex="-1"
    aria-labelledby="alertModalTitle"
    aria-hidden="true"
  >
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="alertModalTitle">{{ title }}</h5>
          <button
            type="button"
            class="btn-close"
            data-bs-dismiss="modal"
            aria-label="Schließen"
          ></button>
        </div>
        <div class="modal-body">{{ message }}</div>
        <div class="modal-footer">
          <button type="button" class="btn btn-primary" data-bs-dismiss="modal">
            {{ closeLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, useTemplateRef } from 'vue';
import { Modal } from 'bootstrap';

/**
 * Hinweismodal ohne Entscheidung — eine Meldung, ein Knopf. Aufruf via
 * `open(message)`, das Promise löst auf, sobald der Benutzer weggeklickt hat.
 * Gegenstück zu ConfirmModal, wo es etwas zu bestätigen gibt.
 */
withDefaults(defineProps<{
  title?:      string;
  closeLabel?: string;
}>(), {
  title:      'Hinweis',
  closeLabel: 'OK',
});

const message = ref('');
const modalEl = useTemplateRef<HTMLDivElement>('modalEl');
let   modal: Modal | null = null;
let   resolver: (() => void) | null = null;
let   autoCloseTimer: number | null = null;

onMounted(() => {
  if (modalEl.value) {
    modal = new Modal(modalEl.value);
    // Deckt alle Schließwege ab (X, OK, Backdrop, ESC).
    modalEl.value.addEventListener('hidden.bs.modal', onHidden);
  }
});

onBeforeUnmount(() => {
  if (autoCloseTimer !== null) window.clearTimeout(autoCloseTimer);
  modalEl.value?.removeEventListener('hidden.bs.modal', onHidden);
  modal?.dispose();
  modal = null;
});

/**
 * `autoCloseMs` schließt die Meldung nach dieser Zeit von selbst — für reine
 * Erfolgsmeldungen, bei denen nichts zu tun ist. Wegklicken geht trotzdem.
 */
function open(msg: string, autoCloseMs?: number): Promise<void> {
  message.value = msg;
  return new Promise<void>(resolve => {
    resolver = resolve;
    modal?.show();
    if (autoCloseMs) autoCloseTimer = window.setTimeout(() => modal?.hide(), autoCloseMs);
  });
}

function onHidden(): void {
  if (autoCloseTimer !== null) window.clearTimeout(autoCloseTimer);
  autoCloseTimer = null;
  resolver?.();
  resolver = null;
}

defineExpose({ open });
</script>
